// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { ScrollView } from 'react-native';

import { Text, View } from '@/components/Themed';

import type { Player, TourTotalsByHoleResponse } from '../types';
import { styles } from '../styles';

type Props = {
  series: TourTotalsByHoleResponse['series'];
  players: Player[];
  holes: TourTotalsByHoleResponse['holes'];
  rounds: TourTotalsByHoleResponse['rounds'];
};

export function ResultsTable({ series, players, holes, rounds }: Props) {
  const nameLookup = new Map(players.map((p) => [p.id, p.name]));
  const headers = holes.map((hole, index) => hole.label ?? hole.Label ?? `${index + 1}`);
  const holesByRound = holes.reduce<Record<string, Array<{ holeNumber: number; label: string }>>>((acc, hole, index) => {
    const roundId = hole.roundId ?? hole.RoundId ?? '';
    if (!roundId) return acc;
    const holeNumber = hole.holeNumber ?? hole.HoleNumber ?? 0;
    const label = hole.label ?? hole.Label ?? `${index + 1}`;
    if (!acc[roundId]) acc[roundId] = [];
    acc[roundId].push({ holeNumber, label });
    return acc;
  }, {});
  const orderedRoundIds = holes.reduce<string[]>((acc, hole) => {
    const roundId = hole.roundId ?? hole.RoundId ?? '';
    if (!roundId || acc.includes(roundId)) return acc;
    acc.push(roundId);
    return acc;
  }, []);
  orderedRoundIds.forEach((roundId) => {
    holesByRound[roundId]?.sort((a, b) => a.holeNumber - b.holeNumber);
  });
  const roundNameById = new Map(
    rounds.map((round) => {
      const roundId = round.roundId ?? round.RoundId ?? '';
      const name = round.name ?? round.Name ?? round.courseName ?? round.CourseName ?? '';
      return [roundId, name];
    })
  );
  const normalizedSeries = series
    .map((entry) => ({
      playerId: entry.playerId ?? entry.PlayerId ?? '',
      totals: entry.totals ?? entry.Totals ?? [],
    }))
    .filter((entry) => entry.playerId);

  return (
    <ScrollView horizontal showsHorizontalScrollIndicator={false}>
      <View>
        <View style={styles.resultsTableRoundHeaderRow}>
          <Text style={[styles.resultsTableCell, styles.resultsTablePlayerCell]} />
          {orderedRoundIds.map((roundId) => {
            const roundName = roundNameById.get(roundId) ?? roundId;
            const shortName = roundName.length > 20 ? `${roundName.slice(0, 20)}…` : roundName;
            const count = holesByRound[roundId]?.length ?? 0;
            const width = count * 40;
            return (
              <View key={`round-${roundId}`} style={[styles.resultsTableRoundHeaderCell, { width }]}>
                <Text style={styles.resultsTableRoundHeaderText}>{shortName}</Text>
              </View>
            );
          })}
        </View>
        <View style={styles.resultsTableHeaderRow}>
          <Text style={[styles.resultsTableCell, styles.resultsTablePlayerCell]}>Player</Text>
          {orderedRoundIds.map((roundId) =>
            (holesByRound[roundId] ?? []).map((hole, idx) => (
              <Text key={`header-${roundId}-${idx}`} style={[styles.resultsTableCell, styles.resultsTableHoleCell]}>
                {hole.holeNumber}
              </Text>
            ))
          )}
        </View>
        {normalizedSeries.map((entry) => (
          <View key={entry.playerId} style={styles.resultsTableRow}>
            <Text style={[styles.resultsTableCell, styles.resultsTablePlayerCell]}>
              {nameLookup.get(entry.playerId) ?? entry.playerId}
            </Text>
            {headers.map((_, idx) => (
              <Text key={`${entry.playerId}-${idx}`} style={[styles.resultsTableCell, styles.resultsTableHoleCell]}>
                {entry.totals[idx] ?? 0}
              </Text>
            ))}
          </View>
        ))}
      </View>
    </ScrollView>
  );
}
